import { FormsySelect } from '@thanx/uikit/select'
import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { getCampaignMetricByType } from 'actions/campaignMetrics'
import { getTargetSegments } from 'actions/targetSegments'
import { useGetSegmentQuery } from 'api/segments'
import { ReactComponent as HollowInfoIcon } from 'assets/images/hollowInfo.svg'
import AIBadge from 'components/AIBadge'
import Counter from 'components/Counter'
import SegmentDetails from 'components/SegmentDetails'
import Spinner from 'components/Spinner'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import { Fields as Campaign } from 'models/Campaign'
import { Type as MetricType, UserCountMetric } from 'models/CampaignMetric'
import { Fields as TargetSegment } from 'models/TargetSegment'
import { useEffect, useMemo } from 'react'
import Helmet from 'react-helmet'
import { useSelector } from 'react-redux'
import { selectCampaignMetricByType } from 'selectors/campaignMetric'
import { selectTargetSegmentsByMerchant } from 'selectors/targetSegment'
import { numberFormatter } from 'utilities/formatters'
import { createFilterOptions } from 'utilities/search'
import './Audience.scss'

type AudienceProps = {
  campaign: Campaign
}

const t = buildTranslate('thanx_campaigns.customAutomated.audience')

const Audience = (props: AudienceProps) => {
  const { campaign } = props

  const targetSegments = useSelector(selectTargetSegmentsByMerchant)
  const dispatch = useDispatch()

  const userCountMetric = useSelector(
    state =>
      selectCampaignMetricByType(
        state,
        campaign.id,
        MetricType.USER_COUNT
      ) as UserCountMetric | null
  )
  const userCount = userCountMetric?.data?.member_count
  const hasValidUserCount = userCount !== undefined && !isNaN(userCount)

  const currentSegment = useMemo(
    () =>
      targetSegments.find(
        segment => segment.id === campaign.config_target_segment_id
      ),
    [targetSegments, campaign.config_target_segment_id]
  )

  const segmentId = currentSegment?.id ? String(currentSegment.id) : ''

  const {
    data: segmentQueryData,
    isFetching: isLoadingDetailedSegment,
    error: detailedSegmentError,
  } = useGetSegmentQuery(
    { segmentId },
    {
      skip: !currentSegment?.id,
      // Force refetch when segmentId changes to avoid stale data
      refetchOnMountOrArgChange: true,
    }
  )

  const detailedSegment = segmentQueryData?.target_segment

  const renderSegmentOption = (segment: TargetSegment) => {
    const isAiGenerated = segment.ai_generated === true

    if (!isAiGenerated) {
      return segment.name
    }

    return (
      <div className="segment-option">
        <span className="segment-option__name">{segment.name}</span>
        <AIBadge />
      </div>
    )
  }

  const options = useMemo(() => {
    const segmentOptions = targetSegments.map(segment => ({
      label: renderSegmentOption(segment),
      value: segment.id,
    }))

    return [
      {
        label: <div className="grey-70">{t('select.no_segment')}</div>,
        value: null,
      },
      ...segmentOptions,
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetSegments])

  const filterOptions = useMemo(
    () =>
      createFilterOptions(
        targetSegments,
        segment => segment.name,
        segment => segment.id
      ),
    [targetSegments]
  )

  useEffect(() => {
    dispatch(getTargetSegments())
  }, [dispatch])

  useEffect(() => {
    dispatch(getCampaignMetricByType(campaign.id, 'user_count'))
  }, [dispatch, campaign.id, campaign.config_target_segment_id])

  const shouldShowReviewNotice = currentSegment?.status === 'review'

  const shouldShowSegmentDetails =
    Boolean(campaign.config_target_segment_id) &&
    Boolean(currentSegment) &&
    !detailedSegmentError &&
    (isLoadingDetailedSegment || Boolean(detailedSegment))

  const shouldShowDescription =
    Boolean(campaign.config_target_segment_id) &&
    Boolean(currentSegment) &&
    (!shouldShowSegmentDetails || Boolean(detailedSegmentError))

  return (
    <div className="container padding-top-huge">
      <Helmet>
        <title>{t('page_title')}</title>
      </Helmet>
      <div className="d-flex flex-space-between mb-xl">
        <div>
          <Text.Header2 color="grey90">{t('title')}</Text.Header2>
          <Text.BodyText3 color="grey70">{t('subtitle')}</Text.BodyText3>
        </div>
        <Switch condition={campaign.type === 'custom' && hasValidUserCount}>
          <Counter
            className="d-block pt-m"
            primaryValue={numberFormatter(userCount)}
            name={t('customers')}
            isLoading={userCountMetric?.isLoading}
            isErrored={userCountMetric?.isErrored}
          />
        </Switch>
      </div>

      <FormsySelect
        // @ts-ignore
        name="config_target_segment_id"
        label={t('select.title')}
        placeholder={t('select.no_segment')}
        options={options}
        filterOptions={filterOptions}
        className="mb-xl"
        value={campaign.config_target_segment_id}
        overrides={{
          Popover: {
            props: {
              overrides: {
                Inner: {
                  style: () => ({
                    maxHeight: '300px',
                  }),
                },
              },
            },
          },
        }}
      />

      <Switch condition={shouldShowReviewNotice}>
        <div className="segment-review-notice mb-xl">
          <div className="segment-review-notice__icon">
            <HollowInfoIcon />
          </div>
          <div className="segment-review-notice__content">
            {t('review_notice')}
          </div>
        </div>
      </Switch>

      <Switch condition={shouldShowDescription && !shouldShowSegmentDetails}>
        <>
          <Text.SmallCaps3 color="grey90" tag="p">
            {t('description.title')}
          </Text.SmallCaps3>
          <Text.BodyText4 color="grey70" className="mb-xl">
            {currentSegment?.description}
          </Text.BodyText4>
        </>
      </Switch>

      <Switch condition={shouldShowSegmentDetails}>
        <Switch
          condition={!isLoadingDetailedSegment && !!detailedSegment}
          fallback={
            isLoadingDetailedSegment ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: '200px' }}
              >
                <Spinner
                  className="d-flex justify-content-center"
                  size="2x"
                  isLoading
                />
              </div>
            ) : null
          }
        >
          <SegmentDetails
            segment={detailedSegment!}
            className="mt-xl"
            id="segment-details"
          />
        </Switch>
      </Switch>
    </div>
  )
}

export default Audience
